import { useState, useEffect, useMemo } from 'react';
import { useBack } from '../hooks';
import api from '../api';

// Export and import.
//
// The director runs production in the app while it is still being built and
// will wipe it at the first release. Export now, install fresh, import back:
// his data carries across, the build-phase edit history does not. Later it is
// a local backup, and sample data for a new company.
//
// Import only runs on a freshly installed app -- his decision, and it is what
// keeps this simple: nothing is ever there to collide with, so there are no
// overwrite rules to explain.

const DARK  = '#0f1117';
const CARD  = '#1e2329';
const BORD  = '#2a3040';
const MUTED = '#6b7280';
const DIM   = '#9ca3af';
const TEXT  = '#f0f2f5';
const TEAL  = '#14b8a6';
const AMBER = '#f59e0b';
const RED   = '#ef4444';

const S = {
  page:  { padding: 24, fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box' },
  wrap:  { maxWidth: 640, margin: '0 auto' },
  back:  { background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 },
  card:  { border: `1px solid ${BORD}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16, background: CARD },
  head:  { padding: '14px 18px', borderBottom: `1px solid ${BORD}` },
  label: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' },
  body:  { padding: '16px 18px' },
  lede:  { fontSize: 13, color: DIM, lineHeight: 1.55, margin: '0 0 14px' },
  row:   { display: 'flex', alignItems: 'center', gap: 11, padding: '8px 2px', borderBottom: `1px solid ${BORD}55` },
  box:   { width: 16, height: 16, accentColor: TEAL, margin: 0, flex: 'none', cursor: 'pointer' },
  name:  { fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0 },
  count: { fontSize: 12, color: MUTED, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap' },
  why:   { fontSize: 11.5, color: TEAL },
  btn:   { background: TEAL, border: 'none', borderRadius: 9, color: '#062320', fontSize: 14, fontWeight: 700,
           padding: '10px 18px', cursor: 'pointer', fontFamily: 'inherit' },
  ghost: { background: 'none', border: `1px solid ${BORD}`, borderRadius: 9, color: DIM, fontSize: 13, fontWeight: 600,
           padding: '9px 14px', cursor: 'pointer', fontFamily: 'inherit' },
  link:  { background: 'none', border: 'none', color: TEAL, fontSize: 12.5, cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
  note:  (c) => ({ fontSize: 12.5, lineHeight: 1.55, color: c, background: `${c}14`, border: `1px solid ${c}44`,
                   borderRadius: 9, padding: '10px 12px', margin: '10px 0 0' }),
};

// Ticking one thing ticks everything it points at -- worked out from each
// section's own list, so the page and the server can never disagree.
function closeOver(keys, sections) {
  const byKey = Object.fromEntries(sections.map(s => [s.key, s]));
  const out = new Set();
  const visit = (k) => { if (!byKey[k] || out.has(k)) return; out.add(k); byKey[k].needs.forEach(visit); };
  keys.forEach(visit);
  return sections.map(s => s.key).filter(k => out.has(k));
}

// The file is named in the director's own time, not the server's. The server
// runs in UTC, and an export taken at 08:19 named "0619" is a small lie.
function localStamp(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

function kb(n) { return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.round(n / 1024)} KB`; }

export default function DataTransfer() {
  const goBack = useBack('/admin');
  const [info, setInfo]       = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  const [picked, setPicked]   = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [exported, setExported]   = useState(null);
  const [exportErr, setExportErr] = useState(null);

  const [files, setFiles]     = useState([]);      // [{ name, size, bundle }]
  const [readErr, setReadErr] = useState([]);
  const [check, setCheck]     = useState(null);
  const [checking, setChecking] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult]   = useState(null);
  const [importErr, setImportErr] = useState(null);

  useEffect(() => {
    api.get('/api/v1/data/sections')
      .then(({ data }) => {
        setInfo(data);
        // Everything except production records, which are a choice.
        setPicked(new Set(data.sections.filter(s => !s.extra).map(s => s.key)));
      })
      .catch(err => setLoadErr(err?.response?.data?.error || 'Could not read what there is to export.'));
  }, []);

  const sections = useMemo(() => info?.sections || [], [info]);
  const included = useMemo(() => closeOver([...picked], sections), [picked, sections]);
  // Sections added only because something ticked needs them, with what needs them.
  const neededBy = useMemo(() => {
    const out = {};
    for (const k of included) {
      if (picked.has(k)) continue;
      out[k] = sections.filter(s => picked.has(s.key) && closeOver([s.key], sections).includes(k)).map(s => s.label);
    }
    return out;
  }, [included, picked, sections]);

  function toggle(key) {
    setExported(null);
    setPicked(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function doExport() {
    setExporting(true); setExportErr(null); setExported(null);
    try {
      const res = await api.get('/api/v1/data/export', {
        params: { sections: included.join(',') }, responseType: 'blob',
      });
      const name = `mouldos-export-${localStamp()}.json`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setExported({ name, size: res.data.size });
    } catch (err) {
      setExportErr('The export did not complete. Nothing was saved — try again.');
    } finally {
      setExporting(false);
    }
  }

  // ── import ────────────────────────────────────────────────────────────────

  async function chooseFiles(e) {
    const list = [...(e.target.files || [])];
    e.target.value = '';
    setCheck(null); setResult(null); setImportErr(null); setConfirm(false);
    const good = [], bad = [];
    for (const f of list) {
      try {
        const bundle = JSON.parse(await f.text());
        good.push({ name: f.name, size: f.size, bundle });
      } catch {
        bad.push(`${f.name} is not a MouldOS export file — it could not be read as one.`);
      }
    }
    setFiles(good); setReadErr(bad);
    if (!good.length) return;
    setChecking(true);
    try {
      const { data } = await api.post('/api/v1/data/import/check', {
        files: good.map(f => ({ name: f.name, bundle: f.bundle })),
      });
      setCheck(data);
    } catch (err) {
      setImportErr(err?.response?.data?.error || 'Those files could not be checked.');
    } finally {
      setChecking(false);
    }
  }

  async function doImport() {
    setImporting(true); setImportErr(null);
    try {
      const { data } = await api.post('/api/v1/data/import', {
        files: files.map(f => ({ name: f.name, bundle: f.bundle })),
      });
      setResult(data);
      setConfirm(false);
    } catch (err) {
      const r = err?.response?.data;
      setImportErr([r?.error, r?.why, r?.note].filter(Boolean).join(' ') || 'The import did not complete. Nothing was written.');
    } finally {
      setImporting(false);
    }
  }

  const total = check ? check.summary.reduce((n, s) => n + s.count, 0) : 0;

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <button onClick={goBack} style={S.back}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: 11 }} /> Back
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>Export &amp; import</h1>
        <p style={{ color: MUTED, fontSize: 14, margin: '0 0 28px' }}>
          Take your data out as a file, or bring it into a freshly installed app.
        </p>

        {loadErr && <div style={S.note(RED)}>{loadErr}</div>}

        {/* ── Export ─────────────────────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.head}>
            <span style={S.label}><i className="fa-solid fa-file-export" style={{ marginRight: 6 }} />Export</span>
          </div>
          <div style={S.body}>
            <p style={S.lede}>
              Tick what to take out. Anything it depends on comes with it, so the file can always be
              imported on its own. The change history is never included.
            </p>

            {!info && !loadErr && <div style={{ color: MUTED, fontSize: 13 }}>Reading what there is…</div>}

            {sections.map((s, i) => {
              const auto = !picked.has(s.key) && included.includes(s.key);
              const on = included.includes(s.key);
              const firstExtra = s.extra && !sections[i - 1]?.extra;
              return (
                <div key={s.key}>
                  {firstExtra && (
                    <div style={{ ...S.label, padding: '16px 2px 4px' }}>Also available</div>
                  )}
                  <label style={{ ...S.row, cursor: auto ? 'default' : 'pointer', opacity: auto ? 0.85 : 1 }}>
                    <input type="checkbox" style={S.box} checked={on} disabled={auto}
                      onChange={() => toggle(s.key)} />
                    <span style={S.name}>
                      {s.label}
                      {auto && (
                        <span style={{ ...S.why, display: 'block', fontWeight: 500 }}>
                          Included because {neededBy[s.key]?.join(' and ')} need{neededBy[s.key]?.length === 1 ? 's' : ''} it
                        </span>
                      )}
                      {s.extra && !auto && (
                        <span style={{ display: 'block', fontSize: 11.5, color: MUTED, fontWeight: 500 }}>
                          Finished runs — actual good parts and cycle times
                        </span>
                      )}
                    </span>
                    <span style={S.count}>{s.detail || s.count}</span>
                  </label>
                </div>
              );
            })}

            {info && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16, flexWrap: 'wrap' }}>
                <button style={{ ...S.btn, opacity: included.length && !exporting ? 1 : 0.5 }}
                  disabled={!included.length || exporting} onClick={doExport}>
                  {exporting ? 'Exporting…' : `Export ${included.length} of ${sections.length}`}
                </button>
                <button style={S.link} onClick={() => setPicked(new Set(sections.map(s => s.key)))}>Tick everything</button>
                <button style={{ ...S.link, color: MUTED }} onClick={() => setPicked(new Set())}>Clear</button>
              </div>
            )}

            {exported && (
              <div style={S.note(TEAL)}>
                <i className="fa-solid fa-check" style={{ marginRight: 7 }} />
                Saved <b>{exported.name}</b> ({kb(exported.size)}). Keep it somewhere safe — it is everything you ticked.
              </div>
            )}
            {exportErr && <div style={S.note(RED)}>{exportErr}</div>}
          </div>
        </div>

        {/* ── Import ─────────────────────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.head}>
            <span style={S.label}><i className="fa-solid fa-file-import" style={{ marginRight: 6 }} />Import</span>
          </div>
          <div style={S.body}>
            {info && !info.fresh ? (
              // Not available here, and said why -- never a greyed-out button
              // with no explanation.
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: AMBER, marginBottom: 6 }}>
                  Import is only available for freshly installed version of the app.
                </div>
                <p style={{ ...S.lede, margin: 0 }}>
                  This app already holds {info.found.map(f => `${f.count} ${f.label}`).join(', ')}.
                  Import is for setting up a new installation from an export — bringing data into one
                  already in use would mix two sets with no way to tell them apart.
                </p>
              </>
            ) : info && info.fresh ? (
              <>
                <p style={S.lede}>
                  Choose one or more export files. Nothing is written until you confirm, and then it
                  all lands at once — or, if anything goes wrong, none of it does.
                </p>
                <label style={{ ...S.ghost, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <i className="fa-solid fa-folder-open" /> Choose files
                  <input type="file" accept=".json,application/json" multiple onChange={chooseFiles}
                    style={{ display: 'none' }} />
                </label>

                {files.length > 0 && (
                  <div style={{ marginTop: 12, fontSize: 12.5, color: DIM }}>
                    {files.map(f => <div key={f.name}>{f.name} <span style={{ color: MUTED }}>· {kb(f.size)}</span></div>)}
                  </div>
                )}
                {readErr.map(m => <div key={m} style={S.note(RED)}>{m}</div>)}
                {checking && <div style={{ marginTop: 12, color: MUTED, fontSize: 13 }}>Checking the files…</div>}

                {check && (
                  <div style={{ marginTop: 14 }}>
                    {check.problems.length > 0 ? (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 600, color: RED }}>
                          These files cannot be imported as they are. Nothing has been written.
                        </div>
                        {check.problems.map((p, i) => (
                          <div key={i} style={S.note(RED)}>
                            {p.file && <b>{p.file}: </b>}{p.why}
                            {p.fix && <div style={{ color: DIM, marginTop: 4 }}>{p.fix}</div>}
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                          Ready to import {total.toLocaleString()} records
                          {check.newest && <span style={{ color: MUTED, fontWeight: 500 }}> · exported {new Date(check.newest).toLocaleString()}</span>}
                        </div>
                        <div style={{ fontSize: 13, color: DIM, lineHeight: 1.7 }}>
                          {check.summary.map(s => <div key={s.table}>{s.count.toLocaleString()} {s.label}</div>)}
                        </div>
                        {check.cleared.map(c => <div key={c} style={S.note(AMBER)}>{c}</div>)}

                        {!result && (!confirm ? (
                          <button style={{ ...S.btn, marginTop: 16 }} onClick={() => setConfirm(true)}>Import</button>
                        ) : (
                          <div style={S.note(AMBER)}>
                            This writes {total.toLocaleString()} records into this app. Import can only be done
                            once, into an empty app — afterwards this page will say it is no longer available.
                            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                              <button style={S.btn} disabled={importing} onClick={doImport}>
                                {importing ? 'Importing…' : 'Yes, import'}
                              </button>
                              <button style={S.ghost} disabled={importing} onClick={() => setConfirm(false)}>Not yet</button>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {result && (
                  <div style={S.note(TEAL)}>
                    <i className="fa-solid fa-check" style={{ marginRight: 7 }} />
                    Imported. {result.summary.map(s => `${s.count} ${s.label}`).join(', ')}.
                    The history of this app begins here.
                  </div>
                )}
                {importErr && <div style={S.note(RED)}>{importErr}</div>}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
